<script setup>
import moment from 'moment-timezone';
import { onMounted, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper.js';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import FileIcon from 'dashboard/components-next/icon/FileIcon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const navigateToNewDraft = async () => {
  const newDraft = await store.dispatch('drafts/create', {});
  const { accountId } = route.params;
  router.push({
    name: 'drafts_compose',
    params: { accountId, id: newDraft.id },
  });
};

const openScheduledCompose = async draftId => {
  const { accountId } = route.params;
  router.push({
    name: 'scheduled_compose',
    params: { accountId, id: draftId },
  });
};

const openConversation = async (conversationId, event) => {
  event?.stopPropagation();
  const path = frontendURL(
    conversationUrl({
      accountId: route.params.accountId,
      id: conversationId,
    })
  );
  router.push({ path });
};

const cancelSend = async scheduledId => {
  await store.dispatch('drafts/cancelScheduled', scheduledId);
  await store.dispatch('drafts/getScheduled');
  useAlert(t('DRAFTS.CANCEL_SEND_SUCCESS'));
};

const scheduledByInbox = computed(() => {
  const allScheduled = store.getters['drafts/getScheduled'];
  return allScheduled.reduce((acc, scheduled) => {
    const group = scheduled.inbox?.id || 'none';
    if (!acc[group]) acc[group] = { inbox: scheduled.inbox, items: [] };
    acc[group].items.push(scheduled);
    return acc;
  }, {});
});

const scheduledAtTimezone = scheduled => {
  return moment(scheduled.scheduled_at)
    .tz(scheduled.timezone || moment.tz.guess())
    .format('MMM DD, YYYY, HH:mm A');
};

const remainingTime = scheduled => {
  const now = moment();
  const scheduledTime = moment(scheduled.scheduled_at).tz(
    scheduled.timezone || moment.tz.guess()
  );
  const duration = moment.duration(scheduledTime.diff(now));
  if (duration.asMilliseconds() <= 0) return t('DRAFTS.SENDING');

  const days = Math.floor(duration.asDays());
  const hours = duration.hours();
  const minutes = duration.minutes();
  let remainStr = '';
  if (days > 0) remainStr += `${days}d `;
  if (hours > 0) remainStr += `${hours}h `;
  if (minutes > 0) remainStr += `${minutes}m`;

  return remainStr.trim() || '0m';
};

onMounted(() => {
  store.dispatch('drafts/getScheduled');
});
</script>

<template>
  <div class="h-full overflow-y-auto">
    <div class="flex flex-col h-full">
      <div class="flex flex-col flex-shrink-0 h-full">
        <div
          class="flex items-center justify-between gap-2 px-3 h-12 border-b border-b-n-weak"
        >
          <div class="flex items-center justify-center min-w-0">
            <h1
              class="text-base font-medium truncate text-n-slate-12"
              :title="$t('SIDEBAR.SCHEDULED')"
            >
              {{ $t('SIDEBAR.SCHEDULED') }}
            </h1>
          </div>
          <div class="flex items-center justify-center min-w-0">
            <NextButton sm @click="navigateToNewDraft">
              {{ $t('DRAFTS.COMPOSE_BUTTON') }}
            </NextButton>
          </div>
        </div>
        <main
          class="flex overflow-y-auto overflow-x-hidden grow w-full bg-n-background p-4"
        >
          <ul
            v-if="Object.keys(scheduledByInbox).length"
            class="w-full list-none"
          >
            <li
              v-for="scheduledInbox in scheduledByInbox"
              :key="scheduledInbox"
              class="bg-n-slate-3 rounded-md p-2 mb-4"
            >
              <h4
                class="text-base font-medium text-n-slate-12 mb-2 py-2 flex items-center ps-3"
              >
                <Avatar
                  :name="scheduledInbox.inbox?.name || $t('DRAFTS.NO_INBOX')"
                  :src="scheduledInbox.inbox?.avatar_url"
                  :size="24"
                  class="mr-3"
                  hide-offline-status
                  rounded-full
                />
                {{ scheduledInbox.inbox?.name || $t('DRAFTS.NO_INBOX') }}
              </h4>
              <ul class="divide-y divide-n-weak w-full list-none">
                <li
                  v-for="scheduled in scheduledInbox.items"
                  :key="scheduled.id"
                  class="p-2 cursor-pointer shadow outline-1 outline outline-n-container group/cardLayout rounded-md bg-n-solid-2"
                  @click="openScheduledCompose(scheduled.id)"
                >
                  <div class="flex flex-row w-full gap-4 items-center">
                    <div
                      v-tooltip.top="$t('DRAFTS.ATTRS.TO')"
                      class="flex-shrink-0 flex flex-row items-center min-w-[12rem]"
                    >
                      <Avatar
                        :name="
                          scheduled.contact?.name || scheduled.contact?.email
                        "
                        :src="scheduled.contact?.thumbnail"
                        :size="36"
                        class="mr-4"
                        hide-offline-status
                        rounded-full
                      />
                      <span class="text-sm truncate max-w-[8rem]">
                        {{
                          scheduled.contact?.name ||
                          scheduled.contact?.email ||
                          scheduled.meta?.to_email ||
                          'N/A'
                        }}
                      </span>
                    </div>
                    <div class="flex-auto min-w-0">
                      <p class="text-sm mb-0 truncate split-words">
                        {{ scheduled.meta?.subject || $t('DRAFTS.NO_SUBJECT') }}
                      </p>
                      <p
                        class="text-xs mb-0 truncate split-words text-n-slate-11"
                      >
                        {{
                          scheduled.content?.substring(0, 100) ||
                          $t('DRAFTS.EMPTY_DRAFTS')
                        }}
                      </p>
                      <p
                        v-if="scheduled.meta?.send_error"
                        class="text-sm mb-0 truncate split-words text-red-600"
                      >
                        {{ scheduled.meta?.send_error }}
                      </p>
                    </div>
                    <div
                      v-if="scheduled.conversation"
                      v-tooltip.top="$t('DRAFTS.ATTRS.OPEN_CONVERSATION')"
                      class="flex flex-col items-end ms-auto"
                    >
                      <button
                        @click="
                          openConversation(scheduled.conversation.id, $event)
                        "
                      >
                        <Icon icon="i-lucide-message-square-share" />
                      </button>
                    </div>
                    <div class="items-start flex flex-row w-fit">
                      <div
                        v-if="scheduled.files?.length"
                        class="flex flex-row items-center gap-2 text-xs border border-woot-500 rounded-full px-2 py-1 w-max text-woot-700 bg-woot-50 dark:bg-woot-800 dark:text-woot-100"
                      >
                        <FileIcon
                          :file-type="scheduled.files[0].content_type"
                        />
                        <span class="truncate max-w-[10rem]">
                          {{ scheduled.files[0].filename }}
                        </span>
                      </div>
                      <span
                        v-if="scheduled.files?.length > 1"
                        class="border border-slate-500 rounded-full text-slate-700 bg-slate-50 w-6 h-6 flex items-center justify-center text-xs ml-1 p-1 dark:border-n-slate-7 dark:text-n-slate-11 dark:bg-n-slate-3"
                      >
                        {{ `+${scheduled.files.length - 1}` }}
                      </span>
                    </div>
                    <div class="flex flex-col items-end ms-auto min-w-[12rem]">
                      <span class="text-xs font-normal text-n-slate-11">
                        {{ scheduled.timezone }}
                      </span>
                      <span
                        v-tooltip.top="$t('DRAFTS.ATTRS.SCHEDULED_AT')"
                        class="text-sm text-woot-700 dark:text-white"
                      >
                        {{ scheduledAtTimezone(scheduled) }}
                      </span>
                    </div>
                    <div class="flex flex-col items-end ms-auto min-w-[5rem]">
                      <span
                        v-tooltip.top="$t('DRAFTS.ATTRS.TIME_REMAINING')"
                        class="text-sm font-bold text-woot-700 dark:text-white"
                      >
                        {{ remainingTime(scheduled) }}
                      </span>
                    </div>
                    <div>
                      <NextButton
                        ruby
                        sm
                        confirmation
                        :label="t('DRAFTS.CANCEL_SEND_BUTTON')"
                        @click.stop="cancelSend(scheduled.id)"
                      />
                    </div>
                  </div>
                </li>
              </ul>
            </li>
          </ul>
          <div
            v-else
            class="flex flex-col items-center justify-center w-full h-full text-n-slate-11"
          >
            <Icon
              icon="i-lucide-calendar-clock"
              class="text-4xl text-n-slate-7"
            />
            <p class="mt-4 text-center">
              {{ $t('DRAFTS.EMPTY_SCHEDULED') }}
            </p>
          </div>
        </main>
      </div>
    </div>
  </div>
</template>
